import React from 'react';
import Header from './Header';
import background from './img/softwarePartners.png';
import MobileNavigation from './MobileNavigation';
import { CopyBlock, dracula } from 'react-code-blocks';
import Footer from './Footer';
const DevSubscriptions = () => {
  return (
    <>
      <MobileNavigation />
      <section className="bg-[#212121]">
        <div
          className="w-full pb-28"
          style={{
            backgroundImage: `url(${background})`,
            backgroundSize: '200% 200%',
            backgroundPosition: 'bottom center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <Header />
          <div className="p-4 row md:p-8 how">
            <h3 className="text-white large">Subscription API Guide</h3>
          </div>
          <div className="flex p-8 md:p-16 row flex-col justify-center gap-6 rounded-[28px] bg-white !mb-10 text-black [&_p]:text-black [&_li]:text-black">
            <p className="light" style={{ textWrap: 'wrap', textWrapMode: 'auto' }}>
              This document outlines the Subscription APIs and the recommended flow for creating and
              managing recurring billing. This guide assumes you have already vaulted a customer and
              stored their payment method.
            </p>

            {/* 1 */}
            <h2 className="text-black large">1. Create a Subscription</h2>
            <p className="light">
              To utilize recurring billing, use the{' '}
              <a
                href="https://api.basyspro.com/iqsaas/v1/scalar/#tag/subscription/POST/api/gateway/%7BgatewayId%7D/subscription"
                target="_blank"
                rel="noreferrer"
                className="links"
              >
                Create Subscription API
              </a>
              .
            </p>

            <h3 className="text-2xl font-medium text-black">1.1 Prerequisites</h3>

            <p className="light">
              Before creating a subscription, ensure the following steps are completed:
            </p>

            <ul className="ml-6 list-disc">
              <li className="text-black light">
                Create a customer using the{' '}
                <a
                  href="https://api.basyspro.com/iqsaas/v1/scalar/#tag/customer/POST/api/gateway/{gatewayId}/customer"
                  target="_blank"
                  rel="noreferrer"
                  className="links"
                >
                  Create Customer API
                </a>
              </li>
              <li className="text-black light">
                Retrieve the customer using the{' '}
                <a
                  href="https://api.basyspro.com/iqsaas/v1/scalar/#tag/customer/GET/api/gateway/{gatewayId}/customer/{customerId}"
                  target="_blank"
                  rel="noreferrer"
                  className="links"
                >
                  Get Customer API
                </a>
              </li>
              <li className="text-black light">
                Store returned identifiers for use in the subscription request
              </li>
            </ul>

            <p className="light">Recommended flow:</p>

            <CopyBlock
              text={`Create Customer → Retrieve Customer (Get by ID) → Create Subscription`}
              language="text"
              theme={dracula}
            />

            <p className="light">
              This ensures you have the required IDs and the most up-to-date customer data.
            </p>

            <h3 className="mt-6 text-2xl font-medium text-black">1.2 Required Identifiers</h3>

            <ul className="ml-6 list-disc">
              <li className="text-black light">
                <strong>customerId</strong> — Returned from Create Customer API
              </li>
              <li className="text-black light">
                <strong>addressId</strong> — Retrieved from Get Customer by ID
              </li>
              <li className="text-black light">
                <strong>customerPaymentMethodId</strong> — Retrieved from Get Customer by ID
              </li>
              <li className="text-black light">
                <strong>processorId</strong> — Identifies the processor used for billing
              </li>
            </ul>

            <p className="light">You can obtain processorId from the <a
                  href="https://api.basyspro.com/iqsaas/v1/scalar/#tag/gateway/GET/api/gateway/%7BgatewayId%7D"
                  target="_blank"
                  rel="noreferrer"
                  className="links"
                >
                  Retrieve Gateway API
                </a>.</p>

            {/* 2 */}
            <h2 className="text-black large">2. Recurrence Configuration</h2>

            <p className="light">
              The recurrence object defines billing frequency, duration, and scheduling.
            </p>

            <h3 className="mt-6 text-2xl font-medium text-black">2.1 Billing Period</h3>
            <p className="light">
              Use <code>billingPeriodId</code> to define the billing frequency:
            </p>

            <div className="overflow-x-auto">
              <table className="w-full text-black tg">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Billing Period</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>1</td>
                    <td>Daily</td>
                  </tr>
                  <tr>
                    <td>2</td>
                    <td>Weekly</td>
                  </tr>
                  <tr>
                    <td>3</td>
                    <td>Bi-Weekly</td>
                  </tr>
                  <tr>
                    <td>4</td>
                    <td>Monthly</td>
                  </tr>
                  <tr>
                    <td>5</td>
                    <td>Quarterly</td>
                  </tr>
                  <tr>
                    <td>6</td>
                    <td>Yearly</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="light">This field is also used when updating a subscription.</p>

            <h3 className="mt-6 text-2xl font-medium text-black">2.2 Subscription Status</h3>

            <p className="light">
              The <code>subscriptionStatusId</code> defines the current state of the subscription:
            </p>

            <div className="overflow-x-auto">
              <table className="w-full text-black tg">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>1</td>
                    <td>Draft</td>
                  </tr>
                  <tr>
                    <td>2</td>
                    <td>Scheduled</td>
                  </tr>
                  <tr>
                    <td>3</td>
                    <td>Trialing</td>
                  </tr>
                  <tr>
                    <td>4</td>
                    <td>Active</td>
                  </tr>
                  <tr>
                    <td>5</td>
                    <td>Overdue</td>
                  </tr>
                  <tr>
                    <td>6</td>
                    <td>Paused</td>
                  </tr>
                  <tr>
                    <td>7</td>
                    <td>Cancelled</td>
                  </tr>
                  <tr>
                    <td>8</td>
                    <td>Deleted</td>
                  </tr>
                  <tr>
                    <td>9</td>
                    <td>Completed</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="callout">
              <p className="mb-0 light">
                <strong>Note:</strong> Subscriptions can only be edited while in Draft status.
                Otherwise, a new subscription must be created.
              </p>
            </div>

            <h3 className="mt-6 text-2xl font-medium text-black">2.3 Schedule Object (Optional)</h3>

            <p className="light">
              For advanced use cases, you can define a custom billing schedule using the schedule
              object.
              <br />
              <br />
              This allows for granular control over execution timing (minutes, hours, days, etc.).
              <br />
              <br />
              In most standard recurring billing scenarios, this is not required.
            </p>

            {/* 3 */}
            <h2 className="text-black large">3. Create Subscription API</h2>

            <p className="light">
              <b>Endpoint</b>
            </p>

            <CopyBlock
              text={`POST /api/gateway/{gatewayId}/subscription`}
              language="bash"
              theme={dracula}
            />

            <p className="mt-4 light">
              <b>Example Request</b>
            </p>

            <CopyBlock
              text={`{
  "customerId": "",
  "subscriptionStatusId": 1,
  "name": "",
  "prefix": "",
  "number": null,
  "message": null,
  "note": null,
  "recurrence": {
    "scheduleId": null,
    "schedule": {
      "minutes": [1],
      "hours": [1],
      "daysOfMonth": [1],
      "daysOfWeek": [1],
      "weeksOfMonth": [1],
      "weeksOfYear": [1],
      "monthsOfYear": [1]
    },
    "billingPeriodId": null,
    "numberOfBillingPeriods": null,
    "trialLengthInDays": 1,
    "invoiceLengthInDays": 1,
    "isAutoRenewed": true,
    "termStartDate": "",
    "termEndDate": null,
    "billingStartDate": "",
    "allowProration": true
  },
  "paymentMethod": {
    "customerPaymentMethodId": null,
    "cardProcessorId": null,
    "achProcessorId": null,
    "isAutoCharged": true
  },
  "addresses": [
    {
      "addressId": null,
      "isRemittance": true,
      "isShipping": true,
      "isBilling": true,
      "firstName": null,
      "lastName": null,
      "company": null,
      "email": null,
      "phone": null,
      "fax": null,
      "addressLine1": null,
      "addressLine2": null,
      "city": null,
      "state": null,
      "postalCode": null,
      "country": null
    }
  ],
  "lineItems": [
    {
      "name": "",
      "description": "",
      "quantity": 1,
      "unitPrice": 1,
      "discount": 1,
      "freightAmount": 1,
      "unitOfMeasureId": 1,
      "localTaxPercent": 1,
      "nationalTaxPercent": 1,
      "commodityCode": null,
      "productId": null
    }
  ],
  "customFields": [
    {
      "customFieldId": 1,
      "customFieldValue": [""]
    }
  ]
}`}
              language="json"
              theme={dracula}
            />

            <h3 className="mt-6 text-2xl font-medium text-black">3.1 Response</h3>

            <p className="light">
              A successful request returns a <code>subscriptionId</code> used for future operations.
            </p>

            <CopyBlock
              text={`{
  "statusCode": "Continue",
  "statusDetails": [
    "…"
  ],
  "data": {
    "subscriptionId": "123e4567-e89b-12d3-a456-426614174000"
  }
}`}
              language="json"
              theme={dracula}
            />

            <div className="my-8 separator" />

            {/* 4 */}
            <h2 className="text-black large">4. Subscription Lifecycle Endpoints</h2>

            <ul className="ml-6 list-disc">
              <li className="text-black light">
                <a
                  href="https://api.basyspro.com/iqsaas/v1/scalar/#tag/subscription/GET/api/gateway/%7BgatewayId%7D/subscription/%7BsubscriptionId%7D"
                  target="_blank"
                  rel="noreferrer"
                  className="links"
                >
                  Retrieve a subscription
                </a>
                <br />
                Retrieve an existing subscription using <code>gatewayId</code> and{' '}
                <code>subscriptionId</code>.
              </li>
              <li className="text-black light">
                <a
                  href="https://api.basyspro.com/iqsaas/v1/scalar/#tag/subscription/PUT/api/gateway/%7BgatewayId%7D/subscription/%7BsubscriptionId%7D"
                  target="_blank"
                  rel="noreferrer"
                  className="links"
                >
                  Update a subscription
                </a>{' '}
                <b> (Draft only)</b>
                <br />
                Update an existing subscription using the request body.
              </li>
              <li className="text-black light">
                <a
                  href="https://api.basyspro.com/iqsaas/v1/scalar/#tag/subscription/DELETE/api/gateway/%7BgatewayId%7D/subscription/%7BsubscriptionId%7D"
                  target="_blank"
                  rel="noreferrer"
                  className="links"
                >
                  Delete a subscription
                </a>
                <br />
                Permanently delete a subscription.
              </li>
              <li className="text-black light">
                <a
                  href="https://api.basyspro.com/iqsaas/v1/scalar/#tag/subscription/POST/api/gateway/%7BgatewayId%7D/subscription/search"
                  target="_blank"
                  rel="noreferrer"
                  className="links"
                >
                  Search subscriptions
                </a>
                <br />
                Search across subscriptions using defined criteria.
              </li>
              <li className="text-black light">
                <a
                  href="https://api.basyspro.com/iqsaas/v1/scalar/#tag/subscription/POST/api/gateway/%7BgatewayId%7D/subscription/%7BsubscriptionId%7D/cancel"
                  target="_blank"
                  rel="noreferrer"
                  className="links"
                >
                  Cancel a subscription
                </a>
                <br />
                Cancel a subscription and optionally apply a refund.
              </li>
              <li className="text-black light">
                <a
                  href="https://api.basyspro.com/iqsaas/v1/scalar/#tag/subscription/PUT/api/gateway/%7BgatewayId%7D/subscription/%7BsubscriptionId%7D/pause"
                  target="_blank"
                  rel="noreferrer"
                  className="links"
                >
                  Pause a subscription
                </a>
                <br />
                Pause an active subscription.
              </li>
              <li className="text-black light">
                <a
                  href="https://api.basyspro.com/iqsaas/v1/scalar/#tag/subscription/PUT/api/gateway/%7BgatewayId%7D/subscription/%7BsubscriptionId%7D/resume"
                  target="_blank"
                  rel="noreferrer"
                  className="links"
                >
                  Resume a subscription
                </a>
                <br />
                Resume a paused subscription.
              </li>
            </ul>

            {/* 5 */}
            <h2 className="text-black large">5. Recommended Flow Summary</h2>

            <ol className="ml-6 list-decimal">
              <li className="text-black light">
                Tokenize payment data (card via iFrame or ACH via API)
              </li>
              <li className="text-black light">Create customer (Customer Vault)</li>
              <li className="text-black light">Retrieve customer (Get by ID)</li>
              <li className="text-black light">
                Create subscription using:
                <ul className="mt-2 ml-6 list-circle">
                  <li className="text-black light">customerId</li>
                  <li className="text-black light">customerPaymentMethodId</li>
                  <li className="text-black light">addressId</li>
                </ul>
              </li>
              <li className="text-black light">Manage lifecycle as needed</li>
            </ol>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default DevSubscriptions;
